/** @file ArtToolbar: style notes, brief mode, counts, briefs, uploads and sheet imports, export, pack. */
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import type { BriefMode } from './artTypes';

/** The puppet's profile, the slot counts, the brief's style notes and tick, and the toolbar actions. */
export type ArtToolbarProps = {
  profile: string; filled: number; total: number;
  style: string; onStyle: (text: string) => void;
  includeFilled: boolean; onIncludeFilled: (on: boolean) => void;
  mode: BriefMode; onMode: (mode: BriefMode) => void;
  onBrief: () => void; onBriefDownload: () => void;
  onFiles: (files: File[]) => void;
  onGuide: () => void; onSheet: (file: File, kind: 'split' | 'overpaint') => void;
  onExport: () => void; onPack: () => void;
};

const MODES: readonly { mode: BriefMode; label: string }[] = [
  { mode: 'slots', label: 'one PNG per slot' }, { mode: 'sheet', label: 'one exploded sheet per facing' },
  { mode: 'guide', label: 'guide sheet: paint over (recommended)' },
];

const FIELD = { flex: 1, minWidth: 0, padding: '3px 6px', fontSize: 12, borderRadius: 4, border: '1px solid #44474f', background: '#15171b', color: '#e6e8ec' };

/** A hidden single-PNG picker handing its file to `onFile`. */
function SheetPicker({ label, pick, onFile }: { label: string; pick: RefObject<HTMLInputElement | null>; onFile: (f: File) => void }): ReactElement {
  return (
    <input
      ref={pick} type="file" hidden aria-label={label} accept="image/png,.png"
      onChange={(e) => { const f = e.target.files?.[0]; if (f !== undefined) onFile(f); e.target.value = ''; }}
    />
  );
}

/**
 * Two rows: the style notes field and the brief mode above, then counts on the left and the actions on the right
 * (briefs, slot PNG upload, the guide sheet, the exploded and overpainted sheet imports, export, pack).
 */
export function ArtToolbar(props: ArtToolbarProps): ReactElement {
  const picker = useRef<HTMLInputElement>(null);
  const splitPick = useRef<HTMLInputElement>(null);
  const paintPick = useRef<HTMLInputElement>(null);
  return (
    <header style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 12px', borderBottom: '1px solid #33363d', fontSize: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label htmlFor="art-style-notes">Style notes</label>
        <input id="art-style-notes" type="text" value={props.style} style={FIELD} onChange={(e) => { props.onStyle(e.target.value); }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={props.includeFilled} onChange={(e) => { props.onIncludeFilled(e.target.checked); }} />
          include filled
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
          Brief
          <select aria-label="Brief mode" value={props.mode} style={{ ...FIELD, flex: 'none' }} onChange={(e) => { props.onMode(e.target.value as BriefMode); }}>
            {MODES.map((m) => <option key={m.mode} value={m.mode}>{m.label}</option>)}
          </select>
        </label>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <strong style={{ fontSize: 14 }}>Art</strong>
        <span>profile <code>{props.profile}</code></span>
        <span data-testid="slot-count">{props.filled} / {props.total} slots filled</span>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={props.onBrief} title="Copy a brief for an image model (ChatGPT etc.)">Art brief</button>
        <button type="button" onClick={props.onBriefDownload} title="Save the brief as art-brief.md">Save art-brief.md</button>
        <button type="button" onClick={() => { picker.current?.click(); }} title="PNGs named drawable.entry.tile.png, or a zip of them">Upload PNGs...</button>
        <button type="button" onClick={props.onGuide} title="The rest pose as coloured regions to paint over (guide.<facing>.png)">Guide sheet</button>
        <button type="button" onClick={() => { splitPick.current?.click(); }} title="One image with the parts spread apart (sheet.<facing>.png)">Split sheet...</button>
        <button type="button" onClick={() => { paintPick.current?.click(); }} title="A guide sheet painted over (painted.<facing>.png)">Overpaint sheet...</button>
        <button type="button" onClick={props.onExport}>Export slots.json</button>
        <button type="button" onClick={props.onPack}>Pack sheet</button>
        <input
          ref={picker} type="file" multiple hidden aria-label="Upload PNGs by file name"
          accept="image/png,.png,application/zip,.zip"
          onChange={(e) => { props.onFiles([...(e.target.files ?? [])]); e.target.value = ''; }}
        />
        <SheetPicker label="Split an exploded sheet" pick={splitPick} onFile={(f) => { props.onSheet(f, 'split'); }} />
        <SheetPicker label="Import an overpainted guide" pick={paintPick} onFile={(f) => { props.onSheet(f, 'overpaint'); }} />
      </div>
    </header>
  );
}
